import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Chip,
  Snackbar,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { School as SchoolIcon, Close as CloseIcon } from '@mui/icons-material';
import LoadingBar from 'react-top-loading-bar';

const ParentTopicProgress = () => {
  // State for display information (no dropdowns)
  const [teacherName, setTeacherName] = useState('');
  const [className, setClassName] = useState('');
  const [sectionName, setSectionName] = useState('');

  // State for subject dropdown
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');

  // State for topics and UI
  const [topics, setTopics] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(false);

  // State for modal
  const [openModal, setOpenModal] = useState(false);
  const [selectedRemark, setSelectedRemark] = useState({ topicName: '', remark: '' });

  // Get values from sessionStorage
  const studentId = Number(sessionStorage.getItem('employeeId'));
  const classId = Number(sessionStorage.getItem('classId'));
  const sectionId = Number(sessionStorage.getItem('sectionId'));
  const token = sessionStorage.getItem('token');

  // Handle opening modal with remark
  const handleViewRemark = (topicName, remark) => {
    setSelectedRemark({ topicName, remark });
    setOpenModal(true);
  };

  // Handle closing modal
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedRemark({ topicName: '', remark: '' });
  };

  // Fetch Subjects
  useEffect(() => {
    const fetchSubjects = async () => {
      if (!studentId) return;

      setLoadingSubjects(true);
      setLoadingBarProgress(30);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/Result/GetClassSubjects_Result`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({
              studentId: studentId,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data)) {
            setSubjects(data);

            // Set class name from first subject (all subjects have same class)
            if (data.length > 0 && data[0].className) {
              setClassName(data[0].className);
            }
          } else {
            setSubjects([]);
          }
          setLoadingBarProgress(100);
        } else {
          setSubjects([]);
          setLoadingBarProgress(0);
        }
      } catch (error) {
        console.error('Error fetching subjects:', error);
        setSubjects([]);
        setLoadingBarProgress(0);
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
  }, [studentId, token]);

  // Fetch Topics when subject is selected
  useEffect(() => {
    const fetchTopics = async () => {
      if (!selectedSubjectId || !classId || !sectionId) return;

      setLoadingTopics(true);
      setLoadingBarProgress(30);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/TeachingTopics/GetByClassSubject`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({
              classId: classId,
              sectionId: sectionId,
              subjectId: selectedSubjectId,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          // Handle different response structures
          const topicList = Array.isArray(data)
            ? data
            : Array.isArray(data.topics)
            ? data.topics
            : [];

          setTopics(topicList);
          setLoadingBarProgress(100);
        } else {
          setTopics([]);
          setLoadingBarProgress(0);
        }
      } catch (error) {
        console.error('Error fetching topics:', error);
        setTopics([]);
        setLoadingBarProgress(0);
      } finally {
        setLoadingTopics(false);
      }
    };

    fetchTopics();
  }, [selectedSubjectId, classId, sectionId, token]);

  // Get teacher and section names from sessionStorage
  useEffect(() => {
    const storedTeacherName = sessionStorage.getItem('teacherName') || 'N/A';
    const storedSectionName = sessionStorage.getItem('sectionName') || 'N/A';

    setTeacherName(storedTeacherName);
    setSectionName(storedSectionName);
  }, []);

  return (
    <Box sx={{ p: 3, minHeight: '100vh' }}>
      <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />
      <Card sx={{ maxWidth: '100%', mx: 'auto', boxShadow: 3, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SchoolIcon sx={{ fontSize: 40, color: 'rgba(75,108,183,1)', mr: 1 }} />
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'rgba(49, 70, 119, 1)' }}>
              Topic Progress
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

            {/* Display Teacher, Class, Section Information */}
          {/* <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <FormControl sx={{ minWidth: '100%' }}>
                <InputLabel shrink sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Teacher
                </InputLabel>
                <Box sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {teacherName}
                  </Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl sx={{ minWidth: '100%' }}>
                <InputLabel shrink sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Class
                </InputLabel>
                <Box sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {className || 'N/A'}
                  </Typography>
                </Box>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl sx={{ minWidth: '100%' }}>
                <InputLabel shrink sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Section
                </InputLabel>
                <Box sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {sectionName}
                  </Typography>
                </Box>
              </FormControl>
            </Grid>
          </Grid> */}

          {/* Subject Dropdown */}
          <Box sx={{ mb: 3 }}>
            <FormControl sx={{ minWidth: 300 }}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubjectId}
                onChange={(e) => setSelectedSubjectId(e.target.value)}
                label="Subject"
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.subjectId} value={subject.subjectId}>
                    {subject.subjectName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Loading State */}
          {(loadingSubjects || loadingTopics) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={40} />
            </Box>
          ) : !selectedSubjectId ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                Please select a subject to view topic progress
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 0, border: '1px solid #e0e0e0' }}>
              <Table sx={{ minWidth: 650 }} aria-label="topic-progress">
                <TableHead>
                  <TableRow sx={{ background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Order</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Topic Name</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Covered By</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Covered On</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Remarks</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 600, p: 2 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {topics.length > 0 ? (
                    topics.map((topic, index) => {
                      const isCompleted = topic.status === 'C';
                      const hasRemark = topic.coveredDescription && topic.coveredDescription.trim() !== '';
                      
                      return (
                        <TableRow
                          key={topic.topicId || index}
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: '#f9f9f9' },
                            '&:hover': { bgcolor: '#f0f0f0' },
                          }}
                        >
                          <TableCell sx={{ p: 2 }}>
                            {topic.topicOrder ?? '-'}
                          </TableCell>
                          <TableCell sx={{ p: 2, fontWeight: 500 }}>
                            {topic.topicName || '—'}
                          </TableCell>
                          <TableCell sx={{ p: 2 }}>
                            {topic.coveredByTeacherName || '—'}
                          </TableCell>
                          <TableCell sx={{ p: 2 }}>
                            {topic.dateCovered || '—'}
                          </TableCell>
                          <TableCell sx={{ p: 2 }}>
                            {hasRemark ? (
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewRemark(topic.topicName, topic.coveredDescription)}
                                sx={{
                                  textTransform: 'none',
                                  borderColor: 'rgba(75,108,183,1)',
                                  color: 'rgba(75,108,183,1)',
                                  '&:hover': {
                                    borderColor: 'rgba(24,40,72,1)',
                                    bgcolor: 'rgba(75,108,183,0.08)',
                                  },
                                }}
                              >
                                View 
                              </Button>
                            ) : (
                              <Typography color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ p: 2 }}>
                            <Chip
                              label={isCompleted ? 'Completed' : 'Pending'}
                              color={isCompleted ? 'success' : 'error'}
                              sx={{ fontWeight: 'bold', minWidth: 100 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                        Topics for this subject are not added yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Remark Modal */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
            color: 'white',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Topic Remarks
          </Typography>
          {/* <IconButton
            onClick={handleCloseModal}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <CloseIcon />
          </IconButton> */}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Topic Name:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'rgba(49, 70, 119, 1)' }}>
              {selectedRemark.topicName}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Remarks:
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: '#f9f9f9',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
              }}
            >
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {selectedRemark.remark || 'No remarks available'}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
              color: 'white',
              textTransform: 'none',
              px: 3,
              '&:hover': {
                background: 'linear-gradient(110deg, rgba(24,40,72,1) 11.3%, rgba(75,108,183,1) 99.3%)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentTopicProgress;
